/**
 * Guest transfer page — "Your upcoming transfers & excursions".
 *
 * A DUPLICATE of the live PHP page (hub-php coportal/public/transfers/
 * index.php, reached at at-mysg.com/<ref> and /cs/<ref>), served by Amassa at
 * amassa.at-mysg.com/<ref>. Content and behaviour follow the PHP page section
 * by section — the line numbers in the comments are that file's — so guests'
 * existing links keep showing the same thing when at-mysg.com moves here.
 *
 * Since 2026-10-29 the found page is a list of BLOCKS (transferLayout.ts,
 * rendered by TransferBlocks.tsx) that Guest Portal ▸ Transfer page edits
 * visually; the default layout is the page as it has always been, block for
 * block. The not-yet and error cards keep their fixed design.
 *
 * It is read-only, like the PHP page: no form, no confirm, no guest write.
 * (The R8 React draft had a "confirm transfer" form and showed the guest
 * name, vehicle and driver; none of that exists on the PHP page, and the
 * endpoint behind the form is gone.)
 *
 * Rendered outside the Shell and every auth guard (App.tsx), and deliberately
 * NOT on the staff theme tokens: this is the agent's page — Kuoni yellow, a
 * white-label accent, or Asian Trails orange on white — whatever theme the
 * staff app happens to be in. Colours come from the brand the server resolved.
 */
import { useEffect, type ReactNode } from 'react';
import { useLocation, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  PublicFetchError,
  errorTitle,
  fetchPublicTransfer,
  foundTitle,
  guestLook,
  noneTitle,
  recosFor,
  sectionOn,
  type PublicTransferView,
} from './publicTransfer.api';
import { PrivacyNotice, TransferPageBody, companyAccentVars, companyLogo } from './TransferBlocks';
import { isTransferRef } from './transferRefPath';
import { COMPANY, waDigits } from '@/shared/company';
import './public-transfer.css';

/** `/transfers/:token` — the older Amassa URL for the same page. */
export function PublicTransferRoute() {
  const { token = '' } = useParams<{ token: string }>();
  return <PublicTransferPage token={token} />;
}

export function PublicTransferPage({ token }: { token: string }) {
  const valid = isTransferRef(token);
  // `?a=<agent id>`: the sample booking (1234567) wears that agent's
  // branding, the way the sample letter that links here does.
  const sampleAgentId = new URLSearchParams(useLocation().search).get('a');
  const query = useQuery({
    queryKey: ['public-transfer', token, sampleAgentId] as const,
    queryFn: ({ signal }) => fetchPublicTransfer(token, signal, sampleAgentId),
    enabled: valid,
    retry: false,
    // The PHP page is a one-shot server render: it never refetches behind
    // the guest's back (and every refetch would spend rate-limit budget).
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  let title: string | null = null;
  let body: ReactNode;
  if (!valid) {
    // index.php:88-90 renderError('Invalid reference', …).
    title = errorTitle('Invalid reference');
    body = <ErrorCard title="Invalid reference" message="The reference format is not recognized." />;
  } else if (query.isPending) {
    body = <div className="ptx-loading" aria-busy="true">Loading…</div>;
  } else if (query.isError) {
    const status = query.error instanceof PublicFetchError ? query.error.status : 500;
    if (status === 429) {
      // Not a PHP state — the PHP page has no rate limit. Same card, plain words.
      title = errorTitle('Too many requests');
      body = <ErrorCard title="Too many requests" message="Please wait a few minutes and try again." />;
    } else if (status === 400) {
      title = errorTitle('Invalid reference');
      body = <ErrorCard title="Invalid reference" message="The reference format is not recognized." />;
    } else {
      // index.php:232-235.
      title = errorTitle('Something went wrong');
      body = (
        <ErrorCard
          title="Something went wrong"
          message="We could not load your transfer details. Please try again later."
        />
      );
    }
  } else if (query.data.state === 'none' || !query.data.brand) {
    title = noneTitle(query.data.companyName);
    body = <NotYetPage view={query.data} />;
  } else {
    title = foundTitle(query.data.brand.name);
    body = <TransferPageBody view={query.data} brand={query.data.brand} token={token} />;
  }

  useGuestDocument(title);
  return body;
}

/**
 * The server already injected the right <title> for link previews; this keeps
 * the tab right on the SPA-only paths (dev server, nginx fallback) and paints
 * the page's light ground behind iOS overscroll instead of the staff ink.
 */
export function useGuestDocument(title: string | null) {
  useEffect(() => {
    if (title) document.title = title;
  }, [title]);
  useEffect(() => {
    const root = document.documentElement;
    const prev = { background: root.style.background, colorScheme: root.style.colorScheme };
    root.style.background = '#f0f2f5';
    root.style.colorScheme = 'light';
    return () => {
      root.style.background = prev.background;
      root.style.colorScheme = prev.colorScheme;
    };
  }, []);
}

// ─── renderNotFound (index.php:446-527) ────────────────────────────────
// One page for "no such ref" and "nothing upcoming", on the company brand.

function NotYetPage({ view }: { view: PublicTransferView }) {
  const portal = view.portal;
  const look = guestLook(portal);
  const noticeLogo = companyLogo(look, view.companyLogoUrl);
  const HOTLINE_MY = look.hotlineMy;
  const HOTLINE_SG = look.hotlineSg;
  const CS_EMAIL_MY = look.csEmailMy;
  // The guides block is the Explore block's twin: off with it; its pills
  // are the tenant's places when the Guest Portal says so.
  const explore = sectionOn(portal, 'explore');
  const places = sectionOn(portal, 'recommendations') ? recosFor(null, portal?.places ?? null) : [];
  return (
    <div className="ptx ptx--notice" style={companyAccentVars(look.accent)}>
      <div className="ptx-notice">
        {noticeLogo && <img src={noticeLogo} alt={view.companyName} className="ptx-notice-logo" />}
        <div className="ptx-notice-card">
          <div className="ptx-notice-icon ptx-notice-icon--wait">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <h1>Your upcoming transfers &amp; excursions</h1>
          <p className="ptx-notice-msg">
            Services for your travel dates are displayed here within{' '}
            <strong>2 days of your arrival</strong>.
            <br />
            Any question regarding your future travels, please contact your designated agent.
          </p>
          <div className="ptx-notice-divider" />
          <div className="ptx-notice-contact">
            Need assistance? We’re available 24/7:
            <br />
            <a href={`https://wa.me/${waDigits(HOTLINE_MY)}`}>{HOTLINE_MY}</a> <span className="ptx-wa-badge">WA</span>
            &nbsp;(Malaysia)
            <br />
            <a href={`https://wa.me/${waDigits(HOTLINE_SG)}`}>{HOTLINE_SG}</a> <span className="ptx-wa-badge">WA</span>
            &nbsp;(Singapore)
            <br />
            <a href={`mailto:${CS_EMAIL_MY}`}>{CS_EMAIL_MY}</a>
          </div>
        </div>

        {explore && (
          <div className="ptx-guides">
            <div className="ptx-guides-title">Explore our Destination Guides</div>
            <div className="ptx-guides-links">
              {places.map((rc) => (
                <a key={rc.slug} href={`/places/${rc.slug}`}>
                  {rc.name}
                </a>
              ))}
              <a href="/guide/malaysia">🇲🇾 Malaysia Guide</a>
              <a href="/guide/singapore">🇸🇬 Singapore Guide</a>
            </div>
          </div>
        )}

        <PrivacyNotice portal={portal} />

        <div className="ptx-notice-footer">
          © {new Date().getFullYear()} {view.companyName} · Powered by{' '}
          <a href="https://amassa.ai" target="_blank" rel="noopener">
            Amassa
          </a>
        </div>
      </div>
    </div>
  );
}

// ─── renderError (index.php:399-441) ───────────────────────────────────

function ErrorCard({ title, message }: { title: string; message: string }) {
  // No payload behind an error: the company profile alone.
  const CS_EMAIL_MY = COMPANY.csEmailMy;
  return (
    <div className="ptx ptx--notice ptx--center" style={companyAccentVars(COMPANY.accent)}>
      <div className="ptx-notice-card ptx-error-card" role="alert">
        <div className="ptx-notice-icon ptx-notice-icon--error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        </div>
        <h1>{title}</h1>
        <p className="ptx-notice-msg">{message}</p>
        <div className="ptx-error-contact">
          Need assistance?
          <br />
          <a href={`mailto:${CS_EMAIL_MY}`}>{CS_EMAIL_MY}</a>
        </div>
      </div>
    </div>
  );
}
